/* Gli stati di ogni contenitore di dati.
 *
 * Cinque, non due. Oltre a pieno e caricamento esistono vuoto, errore e
 * stantìo — l'ultimo è il più importante e il più dimenticato: i dati ci sono,
 * ma non sono di adesso, e chi decide deve saperlo PRIMA di premere.
 *
 * Lo stato vuoto non dice mai "nessun risultato" e basta: dice quale vincolo
 * ha svuotato la lista e offre di allentarlo. Un vuoto senza causa costringe a
 * indovinare quale dei sette filtri ha tolto tutto.
 */

import type { ReactNode } from 'react';
import { Pulsante, IconaAggiorna, IconaAttenzione, IconaCerca } from './Base';
import { daQuanto, ora } from '../lib/formato';
import type { StatoDati } from '../api/usaRichiesta';

/* ---------- caricamento ---------- */

export function Rotella({ dim = 26 }: { dim?: number }) {
  return (
    <span
      className="sb-rotella"
      style={{ width: dim, height: dim, borderWidth: Math.max(2, dim / 10) }}
      role="status"
      aria-label="Caricamento in corso"
    />
  );
}

export function Scheletro({
  larghezza = '100%',
  altezza = 9,
}: {
  larghezza?: string | number;
  altezza?: number;
}) {
  return <span className="sb-scheletro" style={{ display: 'block', width: larghezza, height: altezza }} />;
}

/** Righe fantasma con larghezze diverse: righe tutte uguali si leggono come
 *  un'interfaccia rotta, non come un caricamento. */
export function ScheletroLista({ righe = 4, conIcona = true }: { righe?: number; conIcona?: boolean }) {
  const larghezze: [string, string][] = [
    ['72%', '46%'],
    ['58%', '64%'],
    ['80%', '38%'],
    ['52%', '58%'],
  ];
  return (
    <div className="sb-scheletro-righe" aria-busy="true" aria-label="Caricamento dei risultati">
      {Array.from({ length: righe }, (_, i) => {
        const [a, b] = larghezze[i % larghezze.length];
        return (
          <div className="sb-scheletro-riga" key={i}>
            {conIcona && <span className="sb-scheletro sb-scheletro-riga__icona" />}
            <span className="sb-scheletro-riga__testi">
              <span className="sb-scheletro sb-scheletro-riga__testo" style={{ width: a }} />
              <span className="sb-scheletro sb-scheletro-riga__testo" style={{ width: b }} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Velo che blocca la lista mentre il server rilancia la query.
 * Su 1.507.455 righe l'ordinamento non riordina la pagina: interroga. Lasciare
 * la lista viva e cliccabile durante l'attesa farebbe aprire la riga sbagliata.
 */
export function VeloInterrogazione({ testo }: { testo: string }) {
  return (
    <div className="sb-blocco">
      <Rotella />
      <span className="sb-blocco__testo">{testo}</span>
    </div>
  );
}

/* ---------- vuoto ---------- */

export function StatoVuoto({
  titolo = 'Nessun risultato',
  causa,
  azione,
}: {
  titolo?: string;
  /** Quale vincolo ha svuotato la lista. Senza, il vuoto è un enigma. */
  causa: string;
  azione?: { etichetta: string; onClick: () => void };
}) {
  return (
    <div className="sb-stato">
      <span style={{ color: 'var(--sb-bordo-spento)' }} aria-hidden="true">
        <IconaCerca dim={30} />
      </span>
      <div className="sb-stato__titolo" style={{ color: 'var(--sb-testo-medio)' }}>
        {titolo}
      </div>
      <div className="sb-stato__testo">{causa}</div>
      {azione && (
        <Pulsante variante="tenue" onClick={azione.onClick}>
          {azione.etichetta}
        </Pulsante>
      )}
    </div>
  );
}

/* ---------- errore ---------- */

export function StatoErrore({
  titolo = 'Query non riuscita',
  dettaglio,
  onRiprova,
}: {
  titolo?: string;
  dettaglio?: string;
  onRiprova?: () => void;
}) {
  return (
    <div className="sb-stato" role="alert">
      <span className="sb-stato__medaglione" aria-hidden="true">
        <IconaAttenzione dim={19} />
      </span>
      <div className="sb-stato__titolo">{titolo}</div>
      {dettaglio && <div className="sb-stato__testo sb-stato__testo--mono">{dettaglio}</div>}
      {onRiprova && <Pulsante onClick={onRiprova}>Riprova</Pulsante>}
    </div>
  );
}

/* ---------- stantìo ---------- */

export function StatoStantio({
  letturaA,
  onAggiorna,
  children,
}: {
  letturaA: Date;
  onAggiorna?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="sb-stantio">
      <div className="sb-stantio__intestazione">
        <span className="sb-stantio__punto" aria-hidden="true" />
        <span className="sb-occhiello" style={{ color: 'var(--sb-cautela-chiaro)' }}>
          Stantìo
        </span>
      </div>
      <div className="sb-stantio__valore">{children}</div>
      <div className="sb-stantio__eta">
        letto alle {ora(letturaA)} — {daQuanto(letturaA)}
      </div>
      <div className="sb-stato__testo" style={{ maxWidth: 'none', textAlign: 'left' }}>
        Il valore resta in grigio finché non arriva un aggiornamento. Non decidere su questo numero.
      </div>
      {onAggiorna && (
        <Pulsante variante="cautela" onClick={onAggiorna} style={{ alignSelf: 'flex-start' }}>
          <IconaAggiorna />
          Aggiorna
        </Pulsante>
      )}
    </div>
  );
}

/* ---------- il selettore ---------- */

export interface PropsContenitoreDati {
  stato: StatoDati;
  /** Mostrato quando `stato === 'vuoto'`. Deve dire QUALE vincolo ha svuotato. */
  causaVuoto: string;
  azioneVuoto?: { etichetta: string; onClick: () => void };
  messaggioErrore?: string;
  onRiprova?: () => void;
  /** Forma dello scheletro: una lista o un blocco unico. */
  scheletro?: ReactNode;
  children: ReactNode;
}

/**
 * Sceglie la forma da mostrare. Esiste perché una schermata che scrive a mano
 * `if (loading) … else if (error) …` prima o poi salta il vuoto, e un vuoto
 * senza causa è indistinguibile da un errore silenzioso.
 */
export function ContenitoreDati({
  stato,
  causaVuoto,
  azioneVuoto,
  messaggioErrore,
  onRiprova,
  scheletro,
  children,
}: PropsContenitoreDati) {
  if (stato === 'caricamento') return <>{scheletro ?? <ScheletroLista />}</>;
  if (stato === 'errore')
    return <StatoErrore dettaglio={messaggioErrore} onRiprova={onRiprova} />;
  if (stato === 'vuoto') return <StatoVuoto causa={causaVuoto} azione={azioneVuoto} />;
  return <>{children}</>;
}
